import type { QueryClient } from "@tanstack/react-query";
import type { AdminPermission, CurrentActor } from "../api/session-api";
import { hasPermission } from "../auth/access-policy";
import { adminSurfaceRoutes } from "./admin-routes";

export type WorkspacePhase =
  | "authenticating"
  | "session"
  | "access"
  | "warming"
  | "ready"
  | "degraded";

export interface WorkspaceFailure {
  message: string;
  surface: string;
}

export interface WorkspaceProgress {
  completed: number;
  failures: WorkspaceFailure[];
  label: string;
  phase: WorkspacePhase;
  total: number;
}

export const initialWorkspaceProgress: WorkspaceProgress = {
  completed: 0,
  failures: [],
  label: "Authenticating securely",
  phase: "authenticating",
  total: 0,
};

interface WarmSurface {
  cancel: () => Promise<void>;
  label: string;
  permission: AdminPermission;
  run: () => Promise<void>;
}

interface WarmWorkspaceOptions {
  actor: CurrentActor;
  isCurrent?: () => boolean;
  onProgress: (progress: WorkspaceProgress) => void;
  queryClient: QueryClient;
  timeoutMs?: number;
}

function performanceMark(name: string): void {
  if (typeof performance !== "undefined" && typeof performance.mark === "function") {
    performance.mark(name);
  }
}

function permittedSurfaces(actor: CurrentActor, queryClient: QueryClient): WarmSurface[] {
  return adminSurfaceRoutes
    .filter(({ handle }) => handle.warm && hasPermission(actor, handle.permission))
    .flatMap(({ handle }) => {
      const warmer = handle.warm?.(queryClient);
      if (!warmer) return [];
      return {
        label: handle.label,
        permission: handle.permission,
        cancel: warmer.cancel,
        run: async () => {
          await Promise.all([handle.loadPage(), warmer.run()]);
        },
      };
    });
}

async function runWithTimeout(surface: WarmSurface, timeoutMs: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error("Workspace warm-up timed out.")), timeoutMs);
  });
  try {
    await Promise.race([surface.run(), timeout]);
  } catch (error) {
    await surface.cancel();
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function warmAdminWorkspace({
  actor,
  isCurrent = () => true,
  onProgress,
  queryClient,
  timeoutMs = 12_000,
}: WarmWorkspaceOptions): Promise<WorkspaceProgress> {
  const surfaces = permittedSurfaces(actor, queryClient);
  const failures: WorkspaceFailure[] = [];
  let completed = 0;
  let cursor = 0;

  const publish = (progress: WorkspaceProgress) => {
    if (isCurrent()) onProgress(progress);
  };

  performanceMark("cyra-workspace-warm-start");
  publish({
    completed,
    failures,
    label: surfaces[0] ? `Loading ${surfaces[0].label}` : "Preparing workspace",
    phase: "warming",
    total: surfaces.length,
  });

  const worker = async () => {
    while (cursor < surfaces.length && isCurrent()) {
      const index = cursor;
      cursor += 1;
      const surface = surfaces[index];
      if (!surface) return;
      const markName = surface.label.toLowerCase().replaceAll(/[^a-z0-9]+/gu, "-");
      performanceMark(`cyra-workspace-${markName}-start`);
      try {
        await runWithTimeout(surface, timeoutMs);
        performanceMark(`cyra-workspace-${markName}-complete`);
      } catch (error) {
        performanceMark(`cyra-workspace-${markName}-failed`);
        failures.push({
          message: error instanceof Error ? error.message : "The preload request failed.",
          surface: surface.label,
        });
      }
      completed += 1;
      const next = surfaces[cursor];
      publish({
        completed,
        failures: [...failures],
        label: next ? `Loading ${next.label}` : "Preparing workspace",
        phase: "warming",
        total: surfaces.length,
      });
    }
  };

  await Promise.all(Array.from({ length: Math.min(2, surfaces.length) }, () => worker()));
  const result: WorkspaceProgress = {
    completed,
    failures: [...failures],
    label: failures.length ? "Workspace ready with limited data" : "Workspace ready",
    phase: failures.length ? "degraded" : "ready",
    total: surfaces.length,
  };
  publish(result);
  performanceMark("cyra-workspace-warm-complete");
  return result;
}
